import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientsApi, matchesApi, sessionsApi, onlineIdsApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { ClientTransactionsDialog } from '../shared/ClientTransactionsDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Search, Trophy, Save, X, Eye, Undo2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Client Form Component - Fits in screen without scrollbar
function ClientForm({ client, clients, onSave, onCancel }) {
  const [form, setForm] = useState(client || {
    name: '',
    contact_number: '',
    group_reference: '',
    // Session Commission
    session_user_commission: 0,
    session_commission_mode: 'PAY',
    session_reference: '',
    session_reference_commission: 0,
    // Match Commission
    match_user_commission: 0,
    match_commission_mode: 'PAY',
    match_commission_type: 'NETT',
    match_reference: '',
    match_reference_commission: 0,
    // Patti
    patti_enabled: false,
    patti_reference: '',
    patti_percent: 0,
    // Multiple Patti entries
    patti_entries: [],
    // Dabba
    dabba_enabled: false,
    match_dabba_percent: 0,
    session_dabba_percent: 0,
    // Limit
    limit_unlimited: true,
    limit_amount: null
  });
  
  const [nameError, setNameError] = useState('');
  const [checkingName, setCheckingName] = useState(false);

  // Check for duplicate name when user finishes typing
  const checkDuplicateName = async (name) => {
    if (!name || name.trim() === '') {
      setNameError('');
      return;
    }
    
    // Skip check if editing existing client with same name
    if (client && client.name.toLowerCase() === name.toLowerCase()) {
      setNameError('');
      return;
    }
    
    // Also check locally first against loaded clients
    const localDuplicate = clients.find(
      c => c.name.toLowerCase() === name.toLowerCase() && c.client_id !== client?.client_id
    );
    
    if (localDuplicate) {
      setNameError('Client name already exists');
      return;
    }
    
    // Then verify with backend
    try {
      setCheckingName(true);
      const result = await clientsApi.checkName(name);
      if (result.exists) {
        setNameError('Client name already exists');
      } else {
        setNameError('');
      }
    } catch (error) {
      // If API fails, rely on local check
      setNameError('');
    } finally {
      setCheckingName(false);
    }
  };

  // Debounce the name check
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (form.name) {
        checkDuplicateName(form.name);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [form.name]);

  const handleSubmit = () => {
    if (!form.name) {
      toast.error('Client name is required');
      return;
    }
    
    if (nameError) {
      toast.error(nameError);
      return;
    }
    
    onSave(form);
  };

  // Filter out current client from reference options
  const otherClients = clients.filter(c => c.client_id !== client?.client_id);

  return (
    <div className="space-y-3">
      {/* Row 1: Basic Info + Session Commission */}
      <div className="grid grid-cols-2 gap-3">
        <GroupBox label="Basic Info" className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Client Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({...form, name: e.target.value})}
                className={`h-7 text-sm ${nameError ? 'border-red-500 focus:ring-red-500' : ''}`}
                data-testid="client-name-input"
              />
              {nameError && (
                <p className="text-xs text-red-500 mt-1" data-testid="client-name-error">
                  {nameError}
                </p>
              )}
              {checkingName && (
                <p className="text-xs text-muted-foreground mt-1">Checking...</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input 
                value={form.contact_number || ''} 
                onChange={(e) => setForm({...form, contact_number: e.target.value})}
                className="h-7 text-sm"
                data-testid="client-contact-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Group/Reference</Label>
              <Select 
                value={form.group_reference || "none"} 
                onValueChange={(v) => setForm({...form, group_reference: v === "none" ? "" : v})}
              >
                <SelectTrigger className="h-7 text-sm" data-testid="client-group-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {otherClients.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </GroupBox>

        <GroupBox label="Session Commission" className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">User Comm %</Label>
              <Input 
                type="number" 
                value={form.session_user_commission} 
                onChange={(e) => setForm({...form, session_user_commission: parseFloat(e.target.value) || 0})}
                className="h-7 text-sm"
                data-testid="session-commission-input"
              />
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={form.session_commission_mode} onValueChange={(v) => setForm({...form, session_commission_mode: v})}>
                <SelectTrigger className="h-7 text-sm" data-testid="session-commission-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAY">PAY</SelectItem>
                  <SelectItem value="RECEIVE">RECEIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ref. Name</Label>
              <Select 
                value={form.session_reference || "none"} 
                onValueChange={(v) => setForm({...form, session_reference: v === "none" ? "" : v})}
              >
                <SelectTrigger className="h-7 text-sm" data-testid="session-ref-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {otherClients.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ref. Comm %</Label>
              <Input 
                type="number" 
                value={form.session_reference_commission} 
                onChange={(e) => setForm({...form, session_reference_commission: parseFloat(e.target.value) || 0})}
                className="h-7 text-sm"
                data-testid="session-ref-commission-input"
              />
            </div>
          </div>
        </GroupBox>
      </div>

      {/* Row 2: Match Commission + Patti */}
      <div className="grid grid-cols-2 gap-3">
        <GroupBox label="Match Commission" className="p-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">User Comm %</Label>
              <Input 
                type="number" 
                value={form.match_user_commission} 
                onChange={(e) => setForm({...form, match_user_commission: parseFloat(e.target.value) || 0})}
                className="h-7 text-sm"
                data-testid="match-commission-input"
              />
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={form.match_commission_mode} onValueChange={(v) => setForm({...form, match_commission_mode: v})}>
                <SelectTrigger className="h-7 text-sm" data-testid="match-commission-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAY">PAY</SelectItem>
                  <SelectItem value="RECEIVE">RECEIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.match_commission_type} onValueChange={(v) => setForm({...form, match_commission_type: v})}>
                <SelectTrigger className="h-7 text-sm" data-testid="match-commission-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NETT">NETT</SelectItem>
                  <SelectItem value="ENTRY_WISE">ENTRY_WISE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ref. Name</Label>
              <Select 
                value={form.match_reference || "none"} 
                onValueChange={(v) => setForm({...form, match_reference: v === "none" ? "" : v})}
              >
                <SelectTrigger className="h-7 text-sm" data-testid="match-ref-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {otherClients.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Ref. Comm %</Label>
              <Input 
                type="number" 
                value={form.match_reference_commission} 
                onChange={(e) => setForm({...form, match_reference_commission: parseFloat(e.target.value) || 0})}
                className="h-7 text-sm"
                data-testid="match-ref-commission-input"
              />
            </div>
          </div>
        </GroupBox>

        <GroupBox label="Patti Details" className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 col-span-2">
              <Switch 
                checked={form.patti_enabled} 
                onCheckedChange={(v) => setForm({...form, patti_enabled: v})}
                data-testid="patti-enabled-switch"
              />
              <Label className="text-xs">Patti Enabled</Label>
            </div>
            {form.patti_enabled && (
              <>
                {/* Primary Patti Entry */}
                <div className="grid grid-cols-2 gap-2 p-2 bg-muted/30 rounded">
                  <div>
                    <Label className="text-xs">Patti %</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="100"
                      value={form.patti_percent} 
                      onChange={(e) => setForm({...form, patti_percent: parseFloat(e.target.value) || 0})}
                      className="h-7 text-sm"
                      data-testid="patti-percent-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Patti Reference</Label>
                    <Select 
                      value={form.patti_reference || "none"} 
                      onValueChange={(v) => setForm({...form, patti_reference: v === "none" ? "" : v})}
                    >
                      <SelectTrigger className="h-7 text-sm" data-testid="patti-ref-select">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- None --</SelectItem>
                        {otherClients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Additional Patti Entries */}
                {(form.patti_entries || []).map((entry, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2 p-2 bg-muted/30 rounded relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute -top-1 -right-1 h-5 w-5 p-0 text-red-500 hover:text-red-700"
                      onClick={() => {
                        const newEntries = [...form.patti_entries];
                        newEntries.splice(idx, 1);
                        setForm({...form, patti_entries: newEntries});
                      }}
                    >
                      ×
                    </Button>
                    <div>
                      <Label className="text-xs">Patti % #{idx + 2}</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        max="100"
                        value={entry.percent} 
                        onChange={(e) => {
                          const newEntries = [...form.patti_entries];
                          newEntries[idx] = {...newEntries[idx], percent: parseFloat(e.target.value) || 0};
                          setForm({...form, patti_entries: newEntries});
                        }}
                        className="h-7 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reference #{idx + 2}</Label>
                      <Select 
                        value={entry.reference || "none"} 
                        onValueChange={(v) => {
                          const newEntries = [...form.patti_entries];
                          newEntries[idx] = {...newEntries[idx], reference: v === "none" ? "" : v};
                          setForm({...form, patti_entries: newEntries});
                        }}
                      >
                        <SelectTrigger className="h-7 text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {otherClients.map(c => (
                            <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                
                {/* Add More Patti Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={() => {
                    const newEntries = [...(form.patti_entries || []), { percent: 0, reference: '' }];
                    setForm({...form, patti_entries: newEntries});
                  }}
                  data-testid="add-patti-btn"
                >
                  + Add More Patti
                </Button>
              </>
            )}
          </div>
        </GroupBox>
      </div>

      {/* Row 3: Dabba + Limit + Actions */}
      <div className="grid grid-cols-3 gap-3">
        <GroupBox label="Dabba Details" className="p-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.dabba_enabled} 
                onCheckedChange={(v) => setForm({...form, dabba_enabled: v})}
                data-testid="dabba-enabled-switch"
              />
              <Label className="text-xs">Dabba Enabled</Label>
            </div>
            {form.dabba_enabled && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Match %</Label>
                  <Input 
                    type="number" 
                    value={form.match_dabba_percent} 
                    onChange={(e) => setForm({...form, match_dabba_percent: parseFloat(e.target.value) || 0})}
                    className="h-7 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Session %</Label>
                  <Input 
                    type="number" 
                    value={form.session_dabba_percent} 
                    onChange={(e) => setForm({...form, session_dabba_percent: parseFloat(e.target.value) || 0})}
                    className="h-7 text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </GroupBox>

        <GroupBox label="Limit" className="p-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.limit_unlimited} 
                onCheckedChange={(v) => setForm({...form, limit_unlimited: v, limit_amount: v ? null : 0})}
                data-testid="limit-unlimited-switch"
              />
              <Label className="text-xs">Unlimited</Label>
            </div>
            {!form.limit_unlimited && (
              <div>
                <Label className="text-xs">Limit Amount</Label>
                <Input 
                  type="number" 
                  value={form.limit_amount || 0} 
                  onChange={(e) => setForm({...form, limit_amount: parseFloat(e.target.value) || 0})}
                  className="h-7 text-sm"
                  data-testid="limit-amount-input"
                />
              </div>
            )}
          </div>
        </GroupBox>

        <div className="flex flex-col justify-end gap-2">
          <Button size="sm" onClick={handleSubmit} data-testid="save-client-btn">
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="cancel-client-btn">
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// Match Form Component - Supports multiple teams
function MatchForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '',
    teams: ['', ''],  // Start with 2 empty team slots
    match_time: new Date().toISOString().slice(0, 16)
  });

  const handleTeamChange = (index, value) => {
    const newTeams = [...form.teams];
    newTeams[index] = value;
    setForm({...form, teams: newTeams});
  };

  const handleSubmit = () => {
    const validTeams = form.teams.slice(0, 2).filter(t => t.trim() !== '');
    if (!form.name || validTeams.length !== 2 || !form.match_time) {
      toast.error('Match name, exactly 2 teams, and time are required');
      return;
    }
    onSave({
      name: form.name,
      teams: validTeams,
      match_time: new Date(form.match_time).toISOString()
    });
  };

  return (
    <div className="space-y-3">
      <GroupBox label="Match Details" className="p-2">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Match Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="h-7 text-sm"
                placeholder="e.g., IND vs AUS - 1st Test"
                data-testid="match-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Match Date & Time *</Label>
              <Input 
                type="datetime-local"
                value={form.match_time} 
                onChange={(e) => setForm({...form, match_time: e.target.value})}
                className="h-7 text-sm"
                data-testid="match-time-input"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-xs">Teams * (exactly 2)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {form.teams.slice(0, 2).map((team, index) => (
                <Input 
                  key={index}
                  value={team}
                  onChange={(e) => handleTeamChange(index, e.target.value)}
                  placeholder={`Team ${index + 1}`}
                  className="h-7 text-sm"
                  data-testid={`match-team-${index}-input`}
                />
              ))}
            </div>
          </div>
        </div>
      </GroupBox>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={handleSubmit} data-testid="save-match-btn">Save</Button>
      </div>
    </div>
  );
}

// Session Form Component
function SessionForm({ matches, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '',
    match_id: '',
    session_type: 'RUNS'
  });

  const handleSubmit = () => {
    if (!form.name || !form.match_id) {
      toast.error('Session name and match are required');
      return;
    }
    onSave(form);
  };

  return (
    <div className="space-y-3">
      <GroupBox label="Session Details" className="p-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Label className="text-xs">Session Name *</Label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="h-7 text-sm"
              data-testid="session-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Match *</Label>
            <Select value={form.match_id || "none"} onValueChange={(v) => setForm({...form, match_id: v === "none" ? "" : v})}>
              <SelectTrigger className="h-7 text-sm" data-testid="session-match-select">
                <SelectValue placeholder="Select match" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Select --</SelectItem>
                {matches.filter(m => m.status === 'OPEN').map(m => (
                  <SelectItem key={m.match_id} value={m.match_id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.session_type} onValueChange={(v) => setForm({...form, session_type: v})}>
              <SelectTrigger className="h-7 text-sm" data-testid="session-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RUNS">RUNS</SelectItem>
                <SelectItem value="WICKETS">WICKETS</SelectItem>
                <SelectItem value="OTHER">OTHER</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </GroupBox>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={handleSubmit} data-testid="save-session-btn">Save</Button>
      </div>
    </div>
  );
}

export function MasterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscribe } = useSocketStore();
  
  // Read tab from URL query params or location state
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || location.state?.tab || 'clients';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [clients, setClients] = useState([]);
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [declareDialog, setDeclareDialog] = useState(null);
  const [viewMatchDialog, setViewMatchDialog] = useState(null);
  const [clientTxDialog, setClientTxDialog] = useState({ open: false, clientId: null, clientName: null, matchId: null });

  // Update tab when URL changes (for keyboard shortcuts)
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['clients', 'matches', 'sessions', 'online-ids'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Handle client name click in View Match dialog
  const handleClientClickInViewMatch = (clientId, clientName, matchId) => {
    setClientTxDialog({
      open: true,
      clientId,
      clientName,
      matchId
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsData, matchesData, sessionsData, onlineIdsData] = await Promise.all([
        clientsApi.list(),
        matchesApi.list(),
        sessionsApi.list(),
        onlineIdsApi.list()
      ]);
      setClients(clientsData);
      setMatches(matchesData);
      setSessions(sessionsData);
      setOnlineIds(onlineIdsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const unsubs = [
      subscribe('CLIENT_CREATED', fetchData),
      subscribe('CLIENT_UPDATED', fetchData),
      subscribe('MATCH_CREATED', fetchData),
      subscribe('SESSION_CREATED', fetchData),
      subscribe('MATCH_DECLARED', fetchData),
      subscribe('SESSION_DECLARED', fetchData)
    ];

    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  // Handlers
  const handleSaveClient = async (data) => {
    try {
      if (editingItem) {
        await clientsApi.update(editingItem.client_id, data);
        toast.success('Client updated');
      } else {
        await clientsApi.create(data);
        toast.success('Client created');
      }
      setShowForm(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveMatch = async (data) => {
    try {
      await matchesApi.create(data);
      toast.success('Match created');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveSession = async (data) => {
    try {
      await sessionsApi.create(data);
      toast.success('Session created');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeclareMatch = async (matchId, winningTeam) => {
    try {
      await matchesApi.declare(matchId, winningTeam);
      toast.success('Match declared');
      setDeclareDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to declare match');
    }
  };

  const handleUndeclareMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to undeclare this match? This will revert its status to OPEN.')) {
      return;
    }
    try {
      console.log('Undeclaring match:', matchId);
      await matchesApi.undeclare(matchId);
      toast.success('Match undeclared - status reverted to OPEN');
      fetchData();
    } catch (error) {
      console.error('Undeclare error:', error);
      toast.error(error.message || 'Failed to undeclare match');
    }
  };

  const handleDeleteMatch = async (matchId, matchName) => {
    if (!window.confirm(`Are you sure you want to delete match "${matchName}"? This will hide the match and ALL its transactions, positions, and sessions.`)) {
      return;
    }
    try {
      await matchesApi.delete(matchId);
      toast.success('Match and all related data deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete match');
    }
  };

  const handleViewMatch = async (matchId) => {
    try {
      const matchData = await matchesApi.get(matchId);
      setViewMatchDialog(matchData);
    } catch (error) {
      toast.error(error.message || 'Failed to load match details');
    }
  };

  const handleDeclareSession = async (sessionId, declaredRun) => {
    try {
      await sessionsApi.declare(sessionId, parseInt(declaredRun));
      toast.success('Session declared');
      setDeclareDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || '-';
  };

  // Handle delete client
  const handleDeleteClient = async (client) => {
    if (client.is_system_account) {
      toast.error(`Cannot delete system account "${client.name}". System accounts can only be renamed.`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${client.name}"?`)) {
      return;
    }
    
    try {
      await clientsApi.delete(client.client_id);
      toast.success('Client deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete client');
    }
  };

  // Desktop-style Clients Table Component
  const ClientsTable = ({ clients, onEdit, onDelete }) => {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            {/* Header Row 1: Group Headers */}
            <thead>
              <tr className="bg-slate-200 dark:bg-slate-700">
                <th colSpan={1} className="border px-1 py-1 text-center font-semibold bg-slate-300 dark:bg-slate-600">Act</th>
                <th colSpan={4} className="border px-1 py-1 text-center font-semibold bg-blue-100 dark:bg-blue-900">User Info</th>
                <th colSpan={4} className="border px-1 py-1 text-center font-semibold bg-emerald-100 dark:bg-emerald-900">Session Commission Details</th>
                <th colSpan={5} className="border px-1 py-1 text-center font-semibold bg-cyan-100 dark:bg-cyan-900">Match Commission Details</th>
                <th colSpan={3} className="border px-1 py-1 text-center font-semibold bg-pink-100 dark:bg-pink-900">Patti Details</th>
                <th colSpan={2} className="border px-1 py-1 text-center font-semibold bg-orange-100 dark:bg-orange-900">Dabba Details</th>
                <th colSpan={2} className="border px-1 py-1 text-center font-semibold bg-purple-100 dark:bg-purple-900">Limit</th>
                <th colSpan={1} className="border px-1 py-1 text-center font-semibold bg-slate-300 dark:bg-slate-600">Actions</th>
              </tr>
              {/* Header Row 2: Sub-column Headers */}
              <tr className="bg-slate-100 dark:bg-slate-800 text-[10px]">
                {/* Act */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">✓</th>
                {/* User Info */}
                <th className="border px-1 py-1 text-left whitespace-nowrap">Name</th>
                <th className="border px-1 py-1 text-left whitespace-nowrap">Cell</th>
                <th className="border px-1 py-1 text-left whitespace-nowrap">Phone</th>
                <th className="border px-1 py-1 text-left whitespace-nowrap">Group</th>
                {/* Session Commission */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">User Comm</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Mode</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Ref.</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Ref Comm</th>
                {/* Match Commission */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">User Comm</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Mode</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Type</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Ref.</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Ref Comm</th>
                {/* Patti Details */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">Enabled</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">%</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Ref.</th>
                {/* Dabba Details */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">Match %</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Session %</th>
                {/* Limit */}
                <th className="border px-1 py-1 text-center whitespace-nowrap">Type</th>
                <th className="border px-1 py-1 text-center whitespace-nowrap">Amount</th>
                {/* Actions */}
                <th className="border px-1 py-1 text-center whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={22} className="text-center py-4 text-muted-foreground">No clients found</td>
                </tr>
              ) : (
                clients.map((client, idx) => (
                  <tr 
                    key={client.client_id} 
                    className={`
                      ${idx % 2 === 0 ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-emerald-50/50 dark:bg-emerald-950/20'}
                      ${client.is_system_account ? 'bg-amber-100/50 dark:bg-amber-900/30' : ''}
                      hover:bg-slate-200/50 dark:hover:bg-slate-700/50 cursor-pointer
                    `}
                    onClick={() => onEdit(client)}
                  >
                    {/* Act */}
                    <td className="border px-1 py-0.5 text-center">
                      <input type="checkbox" checked={client.is_active !== false} readOnly className="w-3 h-3" />
                    </td>
                    {/* User Info */}
                    <td className="border px-1 py-0.5 font-medium whitespace-nowrap">
                      {client.name}
                      {client.is_system_account && (
                        <span className="ml-1 text-[9px] bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-1 rounded">
                          {client.system_account_type}
                        </span>
                      )}
                    </td>
                    <td className="border px-1 py-0.5 text-muted-foreground">{client.contact_number || '-'}</td>
                    <td className="border px-1 py-0.5 text-muted-foreground">-</td>
                    <td className="border px-1 py-0.5 text-muted-foreground">{getClientName(client.group_reference)}</td>
                    {/* Session Commission */}
                    <td className="border px-1 py-0.5 text-center bg-emerald-50/30 dark:bg-emerald-950/30">
                      {client.session_user_commission || 0}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-emerald-50/30 dark:bg-emerald-950/30">
                      <span className={client.session_commission_mode === 'PAY' ? 'text-red-600' : 'text-emerald-600'}>
                        {client.session_commission_mode || 'PAY'}
                      </span>
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-emerald-50/30 dark:bg-emerald-950/30">
                      {getClientName(client.session_reference)}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-emerald-50/30 dark:bg-emerald-950/30 font-mono">
                      {client.session_reference_commission || 0}
                    </td>
                    {/* Match Commission */}
                    <td className="border px-1 py-0.5 text-center bg-cyan-50/30 dark:bg-cyan-950/30">
                      {client.match_user_commission || 0}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-cyan-50/30 dark:bg-cyan-950/30">
                      <span className={client.match_commission_mode === 'PAY' ? 'text-red-600' : 'text-emerald-600'}>
                        {client.match_commission_mode || 'PAY'}
                      </span>
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-cyan-50/30 dark:bg-cyan-950/30">
                      {client.match_commission_type || 'NETT'}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-cyan-50/30 dark:bg-cyan-950/30">
                      {getClientName(client.match_reference)}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-cyan-50/30 dark:bg-cyan-950/30 font-mono">
                      {client.match_reference_commission || 0}
                    </td>
                    {/* Patti Details */}
                    <td className="border px-1 py-0.5 text-center bg-pink-50/30 dark:bg-pink-950/30">
                      {client.patti_enabled ? (
                        <span className="text-emerald-600">✓</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-pink-50/30 dark:bg-pink-950/30 font-mono">
                      {client.patti_enabled ? client.patti_percent || 0 : '-'}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-pink-50/30 dark:bg-pink-950/30">
                      {getClientName(client.patti_reference)}
                    </td>
                    {/* Dabba Details */}
                    <td className="border px-1 py-0.5 text-center bg-orange-50/30 dark:bg-orange-950/30 font-mono">
                      {client.dabba_enabled ? client.match_dabba_percent || 0 : '-'}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-orange-50/30 dark:bg-orange-950/30 font-mono">
                      {client.dabba_enabled ? client.session_dabba_percent || 0 : '-'}
                    </td>
                    {/* Limit */}
                    <td className="border px-1 py-0.5 text-center bg-purple-50/30 dark:bg-purple-950/30">
                      {client.limit_unlimited ? 'Unlimited' : 'Limited'}
                    </td>
                    <td className="border px-1 py-0.5 text-center bg-purple-50/30 dark:bg-purple-950/30 font-mono">
                      {client.limit_unlimited ? '∞' : client.limit_amount?.toLocaleString() || 0}
                    </td>
                    {/* Actions */}
                    <td className="border px-1 py-0.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-5 px-1 text-[10px]"
                          onClick={() => onEdit(client)}
                          data-testid={`edit-client-${client.client_id}`}
                        >
                          Edit
                        </Button>
                        {!client.is_system_account && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-5 px-1 text-[10px] text-red-600 hover:text-red-700"
                            onClick={() => onDelete(client)}
                            data-testid={`delete-client-${client.client_id}`}
                          >
                            Del
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Columns for other tabs (matches, sessions, etc.)
  const getTeamsDisplay = (match) => {
    // Support both old format (team_a/team_b) and new format (teams array)
    if (match.teams && match.teams.length > 0) {
      return match.teams.join(' vs ');
    }
    // Fallback for old format
    return `${match.team_a || ''} vs ${match.team_b || ''}`;
  };

  // Helper to get teams array from match (supports both formats)
  const getTeamsArray = (match) => {
    if (match.teams && match.teams.length > 0) {
      return match.teams;
    }
    // Fallback for old format
    return [match.team_a, match.team_b].filter(t => t);
  };

  // Handle live commission toggle
  const handleLiveCommissionToggle = async (matchId, currentValue) => {
    try {
      await matchesApi.updateSettings(matchId, { live_commission: !currentValue });
      toast.success(`Commission mode: ${!currentValue ? 'Live' : 'After Declaration'}`);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update setting');
    }
  };

  const matchColumns = [
    { header: 'Name', accessor: 'name' },
    { header: 'Teams', render: (row) => getTeamsDisplay(row) },
    { header: 'Time', render: (row) => format(new Date(row.match_time), 'dd/MM/yy HH:mm') },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { 
      header: 'Live Comm', 
      render: (row) => {
        const isLive = row.live_commission !== false; // Default true
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Switch 
              checked={isLive}
              onCheckedChange={() => handleLiveCommissionToggle(row.match_id, isLive)}
              className="scale-75"
              data-testid={`live-comm-${row.match_id}`}
            />
            <span className={`text-xs ${isLive ? 'text-green-400' : 'text-muted-foreground'}`}>
              {isLive ? 'Live' : 'Decl'}
            </span>
          </div>
        );
      }
    },
    { header: 'Winner', render: (row) => row.winning_team || '-' },
    { 
      header: 'Actions', 
      render: (row) => {
        const matchId = row?.match_id;
        const matchName = row?.name;
        const status = row?.status;
        
        if (!matchId) return null;
        
        return (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            {status === 'OPEN' ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDeclareDialog({ type: 'match', item: row })}
                data-testid={`declare-match-${matchId}`}
              >
                <Trophy className="h-3.5 w-3.5 mr-1" />
                Declare
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/transactions?match_id=${matchId}&view_only=true`)}
                  data-testid={`view-match-${matchId}`}
                >
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  View
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleUndeclareMatch(matchId)}
                  data-testid={`undeclare-match-${matchId}`}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => handleDeleteMatch(matchId, matchName)}
                  data-testid={`delete-match-${matchId}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Delete
                </Button>
              </>
            )}
          </div>
        );
      }
    }
  ];

  const sessionColumns = [
    { header: 'Name', accessor: 'name' },
    { header: 'Match', render: (row) => matches.find(m => m.match_id === row.match_id)?.name || row.match_id },
    { header: 'Type', accessor: 'session_type' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Result', render: (row) => row.declared_run ?? row.result ?? '-' },
    { 
      header: 'Actions', 
      render: (row) => row.status === 'OPEN' ? (
        <Button 
          variant="outline" 
          size="sm"
          onClick={(e) => { e.stopPropagation(); setDeclareDialog({ type: 'session', item: row }); }}
          data-testid={`declare-session-${row.session_id}`}
        >
          <Trophy className="h-3.5 w-3.5 mr-1" />
          Declare
        </Button>
      ) : (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate(`/transactions?session_id=${row.session_id}&match_id=${row.match_id}&view_only=true`)}
          data-testid={`view-session-${row.session_id}`}
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          View
        </Button>
      )
    }
  ];

  // Filter data - Sort system accounts first
  const filteredClients = clients
    .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // System accounts first
      if (a.is_system_account && !b.is_system_account) return -1;
      if (!a.is_system_account && b.is_system_account) return 1;
      // Then alphabetically
      return a.name.localeCompare(b.name);
    });

  const filteredMatches = matches.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSessions = sessions.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-3" data-testid="master-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Master Data</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 pl-8 w-40 text-sm"
              data-testid="master-search"
            />
          </div>
          <Button 
            size="sm" 
            onClick={() => { setShowForm(true); setEditingItem(null); }}
            data-testid="add-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="clients" data-testid="tab-clients">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches">Matches ({matches.length})</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions ({sessions.length})</TabsTrigger>
          <TabsTrigger value="online-ids" data-testid="tab-online-ids">Online IDs</TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-3">
          {showForm ? (
            <ClientForm 
              client={editingItem} 
              clients={clients}
              onSave={handleSaveClient} 
              onCancel={() => { setShowForm(false); setEditingItem(null); }} 
            />
          ) : (
            <GroupBox label="Clients Master">
              <ClientsTable 
                clients={filteredClients}
                onEdit={(client) => { setEditingItem(client); setShowForm(true); }}
                onDelete={handleDeleteClient}
              />
            </GroupBox>
          )}
        </TabsContent>

        <TabsContent value="matches" className="mt-3">
          {showForm ? (
            <MatchForm 
              onSave={handleSaveMatch} 
              onCancel={() => setShowForm(false)} 
            />
          ) : (
            <GroupBox label="Matches">
              <DenseTable 
                columns={matchColumns} 
                data={filteredMatches}
                emptyMessage="No matches found"
              />
            </GroupBox>
          )}
        </TabsContent>

        <TabsContent value="sessions" className="mt-3">
          {showForm ? (
            <SessionForm 
              matches={matches}
              onSave={handleSaveSession} 
              onCancel={() => setShowForm(false)} 
            />
          ) : (
            <GroupBox label="Sessions">
              <DenseTable 
                columns={sessionColumns} 
                data={filteredSessions}
                emptyMessage="No sessions found"
              />
            </GroupBox>
          )}
        </TabsContent>

        <TabsContent value="online-ids" className="mt-3">
          <GroupBox label="Online IDs">
            <DenseTable 
              columns={[
                { header: 'Name', accessor: 'name' },
                { header: 'Platform', accessor: 'platform' },
                { header: 'Username', accessor: 'username' }
              ]} 
              data={onlineIds}
              emptyMessage="No online IDs found"
            />
          </GroupBox>
        </TabsContent>
      </Tabs>

      {/* Declare Dialog */}
      <Dialog open={!!declareDialog} onOpenChange={() => setDeclareDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {declareDialog?.type === 'match' ? 'Declare Match Winner' : 'Declare Session Result'}
            </DialogTitle>
          </DialogHeader>
          
          {declareDialog?.type === 'match' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select the winning team for: <strong>{declareDialog.item.name}</strong>
              </p>
              <div className="grid grid-cols-2 gap-4">
                {getTeamsArray(declareDialog.item).map((team, index) => (
                  <Button 
                    key={team}
                    variant="outline" 
                    className="h-16"
                    onClick={() => handleDeclareMatch(declareDialog.item.match_id, team)}
                    data-testid={`select-team-${index}`}
                  >
                    <Trophy className="h-5 w-5 mr-2 text-amber-500" />
                    {team}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {declareDialog?.type === 'session' && (
            <DeclareSessionForm 
              session={declareDialog.item}
              onDeclare={handleDeclareSession}
              onCancel={() => setDeclareDialog(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Match Dialog */}
      <Dialog open={!!viewMatchDialog} onOpenChange={() => setViewMatchDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Match Details: {viewMatchDialog?.name}
            </DialogTitle>
          </DialogHeader>
          {viewMatchDialog && (
            <div className="space-y-4">
              {/* Match Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-muted p-2 rounded">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <p className="font-medium"><StatusBadge status={viewMatchDialog.status} /></p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-xs text-muted-foreground">Winner</Label>
                  <p className="font-medium text-emerald-600">{viewMatchDialog.winning_team || '-'}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-xs text-muted-foreground">Match Time</Label>
                  <p className="font-medium text-sm">{viewMatchDialog.match_time ? format(new Date(viewMatchDialog.match_time), 'dd/MM/yy HH:mm') : '-'}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <Label className="text-xs text-muted-foreground">Declared At</Label>
                  <p className="font-medium text-sm">{viewMatchDialog.declared_at ? format(new Date(viewMatchDialog.declared_at), 'dd/MM/yy HH:mm') : '-'}</p>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded">
                  <Label className="text-xs text-amber-600">Transactions</Label>
                  <p className="text-2xl font-bold">{viewMatchDialog.transaction_count || 0}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded">
                  <Label className="text-xs text-blue-600">Positions</Label>
                  <p className="text-2xl font-bold">{viewMatchDialog.position_count || 0}</p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded">
                  <Label className="text-xs text-purple-600">Sessions</Label>
                  <p className="text-2xl font-bold">{viewMatchDialog.session_count || 0}</p>
                </div>
              </div>

              {/* Positions Table */}
              {viewMatchDialog.positions?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Client Positions</Label>
                  <div className="border rounded overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Client</th>
                          <th className="px-2 py-1 text-right">{viewMatchDialog.teams?.[0] || 'Team A'}</th>
                          <th className="px-2 py-1 text-right">{viewMatchDialog.teams?.[1] || 'Team B'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewMatchDialog.positions.map((pos, idx) => (
                          <tr key={idx} className="border-t hover:bg-muted/50">
                            <td className="px-2 py-1">
                              <button 
                                className="text-left text-primary hover:underline cursor-pointer font-medium"
                                onClick={() => handleClientClickInViewMatch(pos.client_id, pos.client_name, viewMatchDialog.match_id)}
                              >
                                {pos.client_name}
                              </button>
                            </td>
                            <td className={`px-2 py-1 text-right font-mono ${pos.team_a_position >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {pos.team_a_position?.toFixed(2)}
                            </td>
                            <td className={`px-2 py-1 text-right font-mono ${pos.team_b_position >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {pos.team_b_position?.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sessions Table */}
              {viewMatchDialog.sessions?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Sessions</Label>
                  <div className="border rounded overflow-hidden max-h-32 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Name</th>
                          <th className="px-2 py-1 text-left">Type</th>
                          <th className="px-2 py-1 text-left">Status</th>
                          <th className="px-2 py-1 text-right">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewMatchDialog.sessions.map((s, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-1">{s.name}</td>
                            <td className="px-2 py-1">{s.session_type}</td>
                            <td className="px-2 py-1"><StatusBadge status={s.status} /></td>
                            <td className="px-2 py-1 text-right font-mono">{s.declared_run ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewMatchDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Transactions Dialog */}
      <ClientTransactionsDialog
        open={clientTxDialog.open}
        onClose={() => setClientTxDialog({ open: false, clientId: null, clientName: null, matchId: null })}
        clientId={clientTxDialog.clientId}
        clientName={clientTxDialog.clientName}
        matchId={clientTxDialog.matchId}
      />
    </div>
  );
}

function DeclareSessionForm({ session, onDeclare, onCancel }) {
  const [run, setRun] = useState('');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Enter the final run for: <strong>{session.name}</strong>
      </p>
      <div>
        <Label className="text-xs">Declared Run</Label>
        <Input 
          type="number" 
          value={run} 
          onChange={(e) => setRun(e.target.value)}
          className="h-8"
          placeholder="Enter final run"
          data-testid="declare-run-input"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onDeclare(session.session_id, run)} data-testid="confirm-declare-session">
          Declare Result
        </Button>
      </DialogFooter>
    </div>
  );
}
